import React from 'react';
import { Tooltip } from 'antd';

/** 圆形进度环周长 r=6.5 → 2π·r ≈ 40.84 */
export const PROGRESS_RING_CIRCUMFERENCE = 2 * Math.PI * 6.5;

/** 灰色 1x1 占位缩略图（loading 态首帧未到时用） */
export const PLACEHOLDER_THUMB =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='1'%3E%3Crect fill='%23999' width='1' height='1'/%3E%3C/svg%3E";

/** 从口令/分享文本中提取 http(s) 链接；整段已是合法 URL 则直接返回 */
export const extractUrlFromText = (text: string): string => {
  const trimmed = text.trim();
  try {
    const u = new URL(trimmed);
    if (u.protocol === 'http:' || u.protocol === 'https:') return trimmed;
  } catch {
    /* not a plain URL */
  }
  const m = trimmed.match(
    /https?:\/\/[^\s一-鿿　-〿＀-￯]+/,
  );
  return m ? m[0] : trimmed;
};

/** 从视频 File 提取首帧 jpeg dataURL */
export const extractVideoFirstFrame = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    video.preload = 'auto';
    video.muted = true;
    video.src = url;

    video.onloadeddata = () => {
      video.currentTime = 0;
    };
    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          URL.revokeObjectURL(url);
          reject(new Error('canvas context unavailable'));
          return;
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
        URL.revokeObjectURL(url);
        resolve(dataUrl);
      } catch (e) {
        URL.revokeObjectURL(url);
        reject(e);
      }
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('video load error'));
    };
  });

/** 从远程 video URL 提取首帧 jpeg dataURL；跨域 canvas tainted 时返回空字符串 */
export const extractVideoFirstFrameFromUrl = (videoUrl: string): Promise<string> =>
  new Promise((resolve) => {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    video.src = videoUrl;

    const timeout = window.setTimeout(() => {
      cleanup();
      resolve('');
    }, 10000);
    const cleanup = () => {
      window.clearTimeout(timeout);
      video.onloadeddata = null;
      video.onseeked = null;
      video.onerror = null;
    };

    video.onloadeddata = () => {
      video.currentTime = 0;
    };
    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 320;
        canvas.height = video.videoHeight || 180;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
          cleanup();
          resolve(dataUrl);
        } else {
          cleanup();
          resolve('');
        }
      } catch {
        cleanup();
        resolve('');
      }
    };
    video.onerror = () => {
      cleanup();
      resolve('');
    };
  });

/** success 态缩略图上的播放图标 overlay */
export const PlayIcon: React.FC = () => (
  <svg className="video-play-icon" viewBox="0 0 16 16" width="16" height="16">
    <circle cx="8" cy="8" r="8" fill="rgba(0,0,0,0.5)" />
    <polygon points="6.5,4.5 6.5,11.5 12,8" fill="#fff" />
  </svg>
);

/** loading 态缩略图上的圆形进度环（白色描边） */
export const ProgressRing: React.FC<{ percent: number }> = ({ percent }) => {
  const p = Math.min(Math.max(percent, 0), 100);
  const offset = PROGRESS_RING_CIRCUMFERENCE * (1 - p / 100);
  return (
    <svg
      className="video-upload-progress-ring"
      viewBox="0 0 16 16"
      width="16"
      height="16"
    >
      <circle
        cx="8"
        cy="8"
        r="6.5"
        fill="none"
        stroke="rgba(255,255,255,0.5)"
        strokeWidth="1.5"
      />
      <circle
        cx="8"
        cy="8"
        r="6.5"
        fill="none"
        stroke="#fff"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeDasharray={PROGRESS_RING_CIRCUMFERENCE}
        strokeDashoffset={offset}
        transform="rotate(-90 8 8)"
      />
    </svg>
  );
};

/** hover 视频预览 tooltip 的 overlay 样式（白底圆角卡片，对齐 V1 tooltipController video-preview 分支） */
const VIDEO_PREVIEW_OVERLAY_STYLE: React.CSSProperties = {
  background: '#fff',
  padding: 0,
  borderRadius: 8,
  boxShadow: '0 2px 12px 0 rgba(153, 153, 153, 0.25)',
};

/**
 * 已填充视频 chip 的 hover 预览。
 *
 * V1 parity（RichTextEditor/nodes/video/hooks.ts:328-389 useVideoPreviewTooltip +
 * core/tooltipController.tsx:1015-1034）：hover success 视频 chip → 弹 120×120 的
 * <video autoPlay loop muted playsInline> 预览。
 *
 * 设计要点：
 * - 用 antd Tooltip（成熟受控组件）接管 hover/定位/portal，不自己监听 mouseover/mouseout，
 *   也不命令式插 DOM —— 与 UploadChip 图片 hover 预览同范式。
 * - <video> 渲染在 Tooltip 的 title 里，hover 时才挂载到 body portal，离开即卸载，
 *   不会有常驻的隐藏 <video> 抢带宽。
 */
export const VideoPreviewTooltip: React.FC<{
  videoUrl: string;
  children: React.ReactElement;
}> = ({ videoUrl, children }) => (
  <Tooltip
    title={
      <div className="rte-video-preview">
        <video src={videoUrl} autoPlay loop muted playsInline />
      </div>
    }
    placement="top"
    arrow={false}
    overlayInnerStyle={VIDEO_PREVIEW_OVERLAY_STYLE}
  >
    {children}
  </Tooltip>
);
